/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderNavigationService,
  CommonActions,
  CommonFlowState,
  ListActionPipelines,
  UsageDialogConfig,
  usageDialogRoute,
} from '@gh/builder-shared';
import {
  BuilderPermissionSet,
  BulkActionEntityType,
  BulkUsageResponse,
  ComponentGridFilter,
  UsageEntityType,
  UsageRestService,
  Usages,
} from '@gh/builder-shared-data';
import {
  AppRouter,
  filterableForm,
  FilterableFormState,
  FormStateBuilder,
  getRouteInitData,
  UserContext,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import {
  DialogService,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { cursorTracker, newGridPaginationCursor } from '@gh/core-ui-grid';
import { ComponentListBusinessService } from '../../resources/component-list-business.service';
import { ComponentDetailsBusinessService } from '../../resources/component-details-business.service';
import {
  ComponentListItem,
  DEFAULT_COMPONENT_FILTER,
} from '../../resources/component-list-item.model';
import { ComponentDetails } from '../../resources/component-and-item-details.model';
import { ComponentGridDefinition } from './component-grid-definition.service';
import { filter$ } from '@gh/rx/operators';

@Component({
  standalone: false,
  selector: 'gh-component-list-tab',
  templateUrl: 'component-list-tab.component.html',
  styleUrls: ['component-list-tab.component.scss'],
  viewProviders: [ComponentGridDefinition],
})
export class ComponentListTabComponent implements OnInit {

  dataSource: SlicedDataSource<ComponentListItem>;
  form: FilterableFormState<ComponentGridFilter>;
  cursor = newGridPaginationCursor();
  loadedData: ComponentListItem[] = [];
  activeLoadedData: ComponentListItem[] = [];
  checkList: any[] = [];

  get isShowDeleteSelected(): boolean {
    return !!this.checkList.length;
  }

  constructor(public gridDefinition: ComponentGridDefinition,
              private userContext: UserContext,
              private formStateBuilder: FormStateBuilder,
              private route: ActivatedRoute,
              private routeData: RouteDataService,
              private navigationService: BuilderNavigationService,
              private businessService: ComponentListBusinessService,
              private commonActions: CommonActions,
              private waitingService: WaitingService,
              private notifications: NotificationService,
              private pipelines: ListActionPipelines,
              private dialogService: DialogService,
              private usageService: UsageRestService,
              private router: AppRouter,
              private componentDetailsBusinessService: ComponentDetailsBusinessService) {
  }

  /**
   * Init component
   */
  ngOnInit(): void {
    const { gridLayout, componentIndex } = getRouteInitData(this.route);

    this.gridDefinition.bind(gridLayout);

    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value: DEFAULT_COMPONENT_FILTER,
      children: {
        showDeleted: void 0,
        searchString: void 0,
      },
    }));

    const tracker = this.routeData.tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor, {
        defaultPageSize: gridLayout.pageSize,
        firstVisibleIndex: componentIndex,
      }));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(() => this.load());
  }

  onEdit(component: ComponentListItem): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.Setup.Estimating.Components.EDIT_VIEW)) {
      this.componentDetailsBusinessService
        .validate(<ID>component.id)
        .pipe(this.waitingService.operator())
        .subscribe(
          (res: ComponentDetails) => {
            if (res && res.warningMessage && res.warningMessage.trim() !== '') {
              this.notifications.warning(res.warningMessage);
              return;
            }
            this.router.navigateToChild(['/setup/estimating/components', component.id]);
          },
          this.notifications.processAll,
        );
    }
  }

  onCreate(): void {
    this.router.navigateToChild(['/setup/estimating/components/new']);
  }

  onClone(component: ComponentListItem): void {
    this.router.navigateToChild(['/setup/estimating/components/new'], { queryParams: { cloneFrom: component.id } });
  }

  onDeactivate(component: ComponentListItem): void {
    const flow = this.pipelines.checkUsageToggleItem({
      messages: {
        confirmUsed: {
          id: 'msg_component_mark_as_deleted',
          params: {
            code: component.code,
          },
        },
        confirmNotUsed: {
          id: 'msg_component_delete',
          params: {
            code: component.code,
          },
        },
      },
      executes: {
        checkUsage: this.commonActions.execute((state: CommonFlowState<any>) =>
          this.businessService.checkUsage(state.value)),
        summary: this.commonActions.execute((state: CommonFlowState<any>) =>
          this.businessService.deactivateOrDelete(component, state.value)),
      },
      finish: () => this.load(),
    });

    flow.run({
      value: component,
    });
  }

  onActivate(component: ComponentListItem): void {
    const flow = this.pipelines.toggleItem({
      question: {
        id: 'msg_component_undelete',
        params: {
          code: component.code,
        },
      },
      execute: this.commonActions.execute((state: CommonFlowState<ComponentListItem>) =>
        this.businessService.activate(state.value)),
      finish: () => this.load(),
    });

    flow.run({
      value: component,
    });
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  onDeleteSelected(): void {
    const entityList = this.loadedData
      .filter((model) => this.checkList.includes(model.id))
      ?.map((item) => ({id: item.id, version: item.version}));

    this.usageService.getBulkUsageInfo(<any[]>entityList, BulkActionEntityType.components)
      .pipe(this.waitingService.operator())
      .subscribe((res: BulkUsageResponse) => {
      this.dialogService.confirmation(
        yesNoConfirmation(res.allDeleted ? 'msg_components_delete_selected' : 'msg_components_delete_mark_as_deleted'),
      ).pipe(
        filter$(yesAnswer),
      ).subscribe(
        () => this.businessService.bulkDelete(res.usages.map((resItem: Usages) => ({
          id: resItem.itemId,
          usage: {
            version: resItem.version,
            deleted: resItem.deleted,
          },
        }))).subscribe(() => this.load(), this.notifications.processAll),
        this.notifications.processAll);
    },           this.notifications.processAll);
  }

  isActionButtonsVisible(): boolean {
    return !this.checkList.length;
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId || void 0,
      entityType: entityType ? entityType : 'COMPONENT',
      label: label ? label : 'lbl_components',
    });
  }

  displayUsage(component: ComponentListItem): void {
    this.dialogService.openWithInput(usageDialogRoute, <UsageDialogConfig>{
      entityId: component.id,
      entityType: UsageEntityType.COMPONENTS_USAGE_INFO,
    });
  }

  rowClicked(event: any): void {
    if (event.preventDefault) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (event.event.shiftKey) {
      this.onCheckChange(event.data.id);
      return;
    }
    if (this.isActionButtonsVisible()) {
      this.onEdit(event.data);
    }
  }

  onToggleSelectAll(): void {
    if (!this.checkList.length || (!!this.checkList.length && this.checkList.length < this.activeLoadedData.length)) {
      this.checkList = this.activeLoadedData?.map((item) => item.id);
      return;
    }
    this.checkList = [];
  }

  onCheckChange(id: ID): void {
    const targetItem = this.loadedData.find((item) => item.id === id);
    if (!targetItem || !this.canBeSelected(targetItem)) {
      return;
    }
    if (this.checkList.includes(id)) {
      this.checkList.splice(this.checkList.indexOf(id), 1);
      this.checkList = [...this.checkList];
      return;
    }
    this.checkList = [...this.checkList, id];
  }

  onLoadData(event: any): void {
    this.checkList = [];
    this.loadedData = event.data;
    this.activeLoadedData = event.data.filter(this.canBeSelected);
  }

  canBeSelected(item: ComponentListItem): boolean {
    return item.active;
  }

  private load(): void {
    this.dataSource = this.businessService.findAllAsDataSource(this.form.submitValue);
  }

}
